import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { audiences } from './audiences';

export default function AudienceNavDropdown({ mobile = false, onNavigate }: {
  mobile?: boolean;
  onNavigate?: () => void;
}) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const id = `bilt-for-navigation-${mobile ? 'mobile' : 'desktop'}`;
  const active = pathname === '/for' || pathname.startsWith('/for/');

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [open]);

  const followLink = () => { setOpen(false); onNavigate?.(); };
  return (
    <div
      ref={root}
      className="relative"
      onMouseEnter={() => { if (!mobile) setOpen(true); }}
      onMouseLeave={() => {
        if (!mobile && !root.current?.contains(document.activeElement)) setOpen(false);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          setOpen(false);
          trigger.current?.focus();
        }
      }}
    >
      <button
        ref={trigger}
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((value) => !value)}
        className={`${active || open ? 'nav-link-active' : 'nav-link-inactive'} ${mobile ? 'w-full justify-between min-h-[44px]' : ''} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400`}
      >
        For
        <ChevronDownIcon aria-hidden="true" className={`h-3.5 w-3.5 transition-transform duration-200 motion-reduce:transition-none ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div id={id} className={mobile ? 'mt-2 ml-3 border-l border-white/15 pl-2' : 'absolute left-0 top-full w-72 pt-2 z-50'}>
          <div className={mobile ? '' : 'rounded-xl border border-white/10 bg-[#0f0f0f] p-2 shadow-elevated'}>
            {audiences.map(({ slug, label, intent }) => {
              const current = pathname.replace(/\/+$/, '') === `/for/${slug}`;
              return (
                <Link
                  key={slug}
                  to={`/for/${slug}`}
                  onClick={followLink}
                  aria-current={current ? 'page' : undefined}
                  className={`block rounded-lg px-3 py-2.5 min-h-[44px] cursor-pointer transition-colors duration-200 motion-reduce:transition-none hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${current ? 'bg-orange-500/[0.08] text-orange-300' : 'text-gray-100'}`}
                >
                  <span className="block text-sm font-medium">{label}</span>
                  <span className="mt-0.5 block text-xs leading-5 text-gray-400">{intent}</span>
                </Link>
              );
            })}
            <Link
              to="/for"
              onClick={followLink}
              aria-current={pathname.replace(/\/+$/, '') === '/for' ? 'page' : undefined}
              className="mt-2 flex min-h-[44px] items-center rounded-lg border-t border-white/10 px-3 text-sm font-medium text-orange-300 hover:bg-white/[0.06] transition-colors duration-200 motion-reduce:transition-none cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
            >
              Find your place in Bilt
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
